import React from "react";
import { motion } from "framer-motion";
import { UserPlus, Search, ShoppingBag, CheckCircle, ArrowRight } from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";

export default function HowItWorksPage() {
  const steps = [
    {
      title: "1. Create an Account",
      description: "Join our community by signing up. Verify your identity to build trust with other users.",
      icon: UserPlus,
      color: "bg-blue-100 text-blue-600"
    },
    {
      title: "2. Find what you need",
      description: "Use our smart search and categories to find high-quality items from verified sellers.",
      icon: Search,
      color: "bg-green-100 text-green-600"
    },
    {
      title: "3. Connect & Transact",
      description: "Message or call sellers directly. Inspect the item in person and pay securely.",
      icon: ShoppingBag,
      color: "bg-purple-100 text-purple-600"
    },
    {
      title: "4. Done!",
      description: "Leave a review for the seller to help others in the community.",
      icon: CheckCircle,
      color: "bg-emerald-100 text-emerald-600"
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900">
      <Header />

      <main>
        {/* Hero Section */}
        <section className="relative py-24 overflow-hidden bg-gray-900 text-white text-center">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-gray-900 via-gray-900 to-green-900/40 -z-10"></div>
          <div className="max-w-7xl mx-auto px-6">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl md:text-7xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-emerald-400"
            >
              How it Works
            </motion.h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              HuluOrder makes it incredibly easy to buy and sell items in Ethiopia. 
              Follow these simple steps to get started.
            </p>
          </div>
        </section>

        {/* Steps Grid */}
        <section className="py-24 max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 relative">
            {/* Connecting lines for desktop */}
            <div className="hidden lg:block absolute top-1/4 left-0 w-full h-0.5 bg-gray-100 -z-10"></div>
            
            {steps.map((step, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex flex-col items-center text-center group"
              >
                <div className={`w-20 h-20 rounded-3xl ${step.color} flex items-center justify-center mb-8 shadow-xl transition-transform group-hover:scale-110 group-hover:-rotate-3`}>
                  <step.icon className="w-10 h-10" />
                </div>
                <h3 className="text-2xl font-bold mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-20 bg-gray-50 text-center">
          <div className="max-w-3xl mx-auto px-6 bg-white p-16 rounded-[4rem] shadow-sm border border-gray-100">
            <h2 className="text-3xl font-bold mb-6 text-gray-900">Ready to start?</h2>
            <p className="text-gray-600 mb-10 text-lg">
              Join thousands of people who are already using HuluOrder to find 
              the best deals in their city.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/register" className="px-8 py-4 bg-green-600 text-white font-bold rounded-2xl hover:bg-green-700 transition flex items-center justify-center gap-2">
                Start Selling <ArrowRight className="w-5 h-5" />
              </a>
              <a href="/" className="px-8 py-4 bg-gray-900 text-white font-bold rounded-2xl hover:bg-black transition">
                Browse Items
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
